import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { getToken, clientPath } from "../lib/api";
import { copyToClipboard } from "../lib/clipboard";
import { ArrowLeft, Copy, FileText, Plus, Trash2, Download, RefreshCw, Pencil, Save, Copy as Duplicate, CalendarPlus, Eye, EyeOff } from "lucide-react";
import TimePicker from "../components/TimePicker";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function EditClientPanel({ client, onSaved, onClose }) {
  const [d, setD] = useState({
    name: client.name || "", phone: client.phone || "", email: client.email || "",
    goal: client.goal || "", notes: client.notes || "",
    consultation_notes: client.consultation_notes || "",
    paid_until: client.paid_until || "",
    package_name: client.package_name || "",
    follow_up_day: client.follow_up_day || "",
    next_follow_up_date: client.next_follow_up_date || "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...d };
      payload.paid_until = d.paid_until || null;
      payload.next_follow_up_date = d.next_follow_up_date || null;
      const { data } = await api.put(`/admin/clients/${client.id}`, payload);
      toast.success("Client details saved");
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[hsl(var(--brand-ink))]/40 p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl border border-[hsl(var(--brand-border))] w-full max-w-xl p-8 shadow-xl max-h-[92vh] overflow-y-auto" data-testid="edit-client-form">
        <div className="font-display text-3xl mb-5">Edit {client.name}</div>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            { k: "name", label: "Name" },
            { k: "phone", label: "Phone" },
            { k: "email", label: "Email", type: "email" },
            { k: "goal", label: "Goal" },
            { k: "package_name", label: "Package" },
            { k: "paid_until", label: "Paid until", type: "date" },
            { k: "follow_up_day", label: "Follow-up day", select: WEEKDAYS },
            { k: "next_follow_up_date", label: "Next follow-up", type: "date" },
          ].map((f) => (
            <div key={f.k}>
              <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1.5">{f.label}</label>
              {f.select ? (
                <select value={d[f.k]} onChange={(e) => setD({ ...d, [f.k]: e.target.value })} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md bg-white" data-testid={`edit-${f.k}`}>
                  <option value="">—</option>
                  {f.select.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type={f.type || "text"} value={d[f.k]} onChange={(e) => setD({ ...d, [f.k]: e.target.value })} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md" data-testid={`edit-${f.k}`} />
              )}
            </div>
          ))}
        </div>
        <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mt-4 mb-1.5">Quick notes</label>
        <textarea rows={2} value={d.notes} onChange={(e) => setD({ ...d, notes: e.target.value })} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md" data-testid="edit-notes" />
        <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mt-4 mb-1.5">Opening consultation notes</label>
        <textarea rows={8} value={d.consultation_notes} onChange={(e) => setD({ ...d, consultation_notes: e.target.value })} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md" data-testid="edit-consultation-notes" />
        <div className="flex gap-3 justify-end mt-5">
          <button type="button" onClick={onClose} className="px-5 py-2 rounded-full hover:bg-[hsl(var(--brand-cream-deep))]">Cancel</button>
          <button disabled={saving} type="submit" className="px-5 py-2 rounded-full bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage-hover))]" data-testid="edit-client-save">
            <Save className="w-4 h-4 inline -mt-0.5 mr-1" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ScheduleFollowUpModal({ clientId, clientName, onClose, onCreated }) {
  const [date, setDate] = useState(new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10));
  const [time, setTime] = useState("");
  const [recurring, setRecurring] = useState(true);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/events", {
        client_id: clientId,
        title: `Follow-up · ${clientName}`,
        type: "follow_up",
        date, time, notes: "",
        recurring_weekly: recurring,
      });
      toast.success(recurring ? "Weekly follow-up scheduled" : "Follow-up scheduled");
      onCreated();
      onClose();
    } catch (err) {
      toast.error("Failed");
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[hsl(var(--brand-ink))]/40 p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-sm p-7 border border-[hsl(var(--brand-border))] shadow-xl" data-testid="schedule-followup-form">
        <div className="font-display text-2xl mb-4">Schedule follow-up</div>
        <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1.5">Date</label>
        <input type="date" required value={date} onChange={(e) => setDate(e.target.value)} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md mb-3" data-testid="followup-date" />
        <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1.5">Time (optional)</label>
        <div className="mb-4">
          <TimePicker value={time} onChange={setTime} testId="followup-time" />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none mb-4">
          <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} data-testid="followup-recurring" />
          <span>Repeat weekly on the same day</span>
        </label>
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-5 py-2 rounded-full hover:bg-[hsl(var(--brand-cream-deep))]">Cancel</button>
          <button type="submit" className="px-5 py-2 rounded-full bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage-hover))]" data-testid="followup-save">
            Schedule
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminClientDetail() {
  const { clientId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [scheduling, setScheduling] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/admin/clients/${clientId}`);
    setData(data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  if (!data) return <div className="font-serif-italic text-[hsl(var(--brand-muted))]">Loading…</div>;
  const { client, plans } = data;

  const copyLink = () => {
    const url = `${window.location.origin}${clientPath(client.name, client.magic_token)}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };
  const regenerate = async () => {
    if (!window.confirm("Regenerate link? The old one will stop working.")) return;
    const { data: r } = await api.post(`/admin/clients/${clientId}/regenerate-token`);
    toast.success("New link generated");
    setData((d) => ({ ...d, client: { ...d.client, magic_token: r.magic_token } }));
  };
  const del = async () => {
    if (!window.confirm("Delete this client and all their plans?")) return;
    await api.delete(`/admin/clients/${clientId}`);
    toast.success("Client deleted");
    nav("/admin/clients");
  };
  const deletePlan = async (planId) => {
    if (!window.confirm("Delete this plan?")) return;
    await api.delete(`/admin/plans/${planId}`);
    toast.success("Plan deleted");
    load();
  };
  const duplicatePlan = async (planId) => {
    const { data } = await api.post(`/admin/plans/${planId}/duplicate`);
    toast.success("Plan duplicated — shifted to next week");
    load();
    nav(`/admin/plans/${data.id}/edit`);
  };
  const togglePlanVisibility = async (planId, currentlyVisible) => {
    const next = !currentlyVisible;
    await api.patch(`/admin/plans/${planId}/visibility?visible=${next}`);
    toast.success(next ? "Visible to client" : "Hidden from client");
    load();
  };
  const downloadPdf = (planId) => {
    const t = getToken();
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/plans/${planId}/pdf?auth=${t}`;
    window.open(url, "_blank");
  };

  const clientUrl = `${window.location.origin}${clientPath(client.name, client.magic_token)}`;

  return (
    <div data-testid="admin-client-detail-page">
      <Link to="/admin/clients" className="text-sm text-[hsl(var(--brand-muted))] hover:text-[hsl(var(--brand-ink))] inline-flex items-center gap-1" data-testid="back-to-clients">
        <ArrowLeft className="w-3 h-3" /> All clients
      </Link>
      <div className="mt-5 flex flex-wrap justify-between gap-6">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--brand-terracotta))]">Client</div>
          <h1 className="font-display text-5xl tracking-tight mt-1">{client.name}</h1>
          <p className="text-[hsl(var(--brand-muted))] mt-2">
            {client.phone || "No phone"} · {client.email || "No email"}
          </p>
          {client.goal ? (
            <div className="mt-3 font-serif-italic text-[hsl(var(--brand-sage))]">Goal — {client.goal}</div>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setEditing(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-[hsl(var(--brand-border))] hover:bg-[hsl(var(--brand-cream))] text-sm" data-testid="edit-client-btn">
            <Pencil className="w-4 h-4" /> Edit details
          </button>
          <button onClick={() => setScheduling(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-[hsl(var(--brand-border))] hover:bg-[hsl(var(--brand-cream))] text-sm" data-testid="schedule-followup-btn">
            <CalendarPlus className="w-4 h-4" /> Schedule follow-up
          </button>
          <Link
            to={`/admin/clients/${clientId}/plans/new`}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage-hover))]"
            data-testid="create-plan-btn"
          >
            <Plus className="w-4 h-4" /> New plan
          </Link>
          <button
            onClick={del}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm text-[hsl(var(--destructive))] hover:bg-red-50"
            data-testid="delete-client-btn"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </div>

      {/* Key facts */}
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border border-[hsl(var(--brand-border))] rounded-xl p-4">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[hsl(var(--brand-muted))]">Package</div>
          <div className="mt-1 font-display text-xl">{client.package_name || "—"}</div>
        </div>
        <div className="bg-white border border-[hsl(var(--brand-border))] rounded-xl p-4">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[hsl(var(--brand-muted))]">Paid until</div>
          <div className="mt-1 font-display text-xl">{client.paid_until || "—"}</div>
        </div>
        <div className="bg-white border border-[hsl(var(--brand-border))] rounded-xl p-4">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[hsl(var(--brand-muted))]">Follow-up day</div>
          <div className="mt-1 font-display text-xl">{client.follow_up_day || "—"}</div>
        </div>
        <div className="bg-white border border-[hsl(var(--brand-border))] rounded-xl p-4">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[hsl(var(--brand-muted))]">Next follow-up</div>
          <div className="mt-1 font-display text-xl">{client.next_follow_up_date || "—"}</div>
        </div>
      </div>

      {client.consultation_notes ? (
        <div className="mt-8 bg-[hsl(var(--brand-cream-deep))] border border-[hsl(var(--brand-border))] rounded-xl p-6">
          <div className="text-xs tracking-[0.2em] uppercase text-[hsl(var(--brand-muted))]">Opening consultation notes</div>
          <div className="mt-2 whitespace-pre-wrap text-[15px] leading-relaxed text-[hsl(var(--brand-ink))]">{client.consultation_notes}</div>
        </div>
      ) : null}

      {client.notes ? (
        <div className="mt-4 bg-white border border-[hsl(var(--brand-border))] rounded-xl p-4 text-sm text-[hsl(var(--brand-muted))]">
          {client.notes}
        </div>
      ) : null}

      {/* Magic link card */}
      <section className="mt-8 bg-[hsl(var(--brand-cream-deep))] rounded-xl border border-[hsl(var(--brand-border))] p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[hsl(var(--brand-muted))]">Private link</div>
            <div className="mt-1 font-mono text-sm break-all text-[hsl(var(--brand-ink))]" data-testid="client-magic-link">{clientUrl}</div>
            <div className="text-xs text-[hsl(var(--brand-muted))] mt-2">Send this on WhatsApp. Only this client can open it.</div>
          </div>
          <div className="flex gap-2">
            <button onClick={copyLink} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[hsl(var(--brand-border))] text-sm hover:bg-[hsl(var(--brand-cream))]" data-testid="copy-magic-link-btn">
              <Copy className="w-3.5 h-3.5" /> Copy
            </button>
            <button onClick={regenerate} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[hsl(var(--brand-border))] text-sm hover:bg-[hsl(var(--brand-cream))]" data-testid="regen-link-btn">
              <RefreshCw className="w-3.5 h-3.5" /> Regenerate
            </button>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="mt-10">
        <h2 className="font-display text-3xl">Weekly plans</h2>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          {plans.length === 0 ? (
            <div className="col-span-2 font-serif-italic text-[hsl(var(--brand-muted))] bg-white rounded-xl border border-[hsl(var(--brand-border))] p-10 text-center">
              No plans yet. Create {client.name.split(" ")[0]}'s first weekly plan.
            </div>
          ) : plans.map((p) => {
            const visible = p.visible_to_client !== false;
            return (
            <div key={p.id} className="bg-white border border-[hsl(var(--brand-border))] rounded-xl p-6 card-lift" data-testid={`plan-card-${p.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-[hsl(var(--brand-sage))]">
                  <FileText className="w-4 h-4" />
                  <div className="text-xs tracking-[0.2em] uppercase">{p.start_date} — {p.end_date}</div>
                </div>
                <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full ${visible ? "bg-[hsl(var(--brand-sage))]/10 text-[hsl(var(--brand-sage))]" : "bg-[hsl(var(--brand-cream-deep))] text-[hsl(var(--brand-muted))]"}`}>
                  {visible ? "Visible" : "Hidden"}
                </span>
              </div>
              <div className="font-display text-2xl mt-2">{p.title}</div>
              {p.notes ? <p className="text-sm text-[hsl(var(--brand-muted))] mt-2 line-clamp-2">{p.notes}</p> : null}
              <div className="mt-5 flex items-center gap-2 flex-wrap">
                <Link to={`/admin/plans/${p.id}/edit`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[hsl(var(--brand-sage))] text-white text-sm hover:bg-[hsl(var(--brand-sage-hover))]" data-testid={`edit-plan-${p.id}`}>
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </Link>
                <button onClick={() => duplicatePlan(p.id)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[hsl(var(--brand-terracotta))]/10 text-[hsl(var(--brand-terracotta))] text-sm hover:bg-[hsl(var(--brand-terracotta))]/20 border border-[hsl(var(--brand-terracotta))]/30" data-testid={`duplicate-plan-${p.id}`}>
                  <Duplicate className="w-3.5 h-3.5" /> Duplicate for next week
                </button>
                <button onClick={() => downloadPdf(p.id)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[hsl(var(--brand-border))] text-sm hover:bg-[hsl(var(--brand-cream))]" data-testid={`pdf-plan-${p.id}`}>
                  <Download className="w-3.5 h-3.5" /> PDF
                </button>
                <button onClick={() => togglePlanVisibility(p.id, visible)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-[hsl(var(--brand-border))] text-xs hover:bg-[hsl(var(--brand-cream))]" data-testid={`toggle-visibility-${p.id}`}>
                  {visible ? <><EyeOff className="w-3.5 h-3.5" /> Hide from client</> : <><Eye className="w-3.5 h-3.5" /> Show to client</>}
                </button>
                <button onClick={() => deletePlan(p.id)} className="ml-auto text-xs text-[hsl(var(--destructive))] hover:underline" data-testid={`delete-plan-${p.id}`}>
                  Delete
                </button>
              </div>
            </div>
            );
          })}
        </div>
      </section>

      {editing ? (
        <EditClientPanel
          client={client}
          onClose={() => setEditing(false)}
          onSaved={(c) => setData((d) => ({ ...d, client: { ...d.client, ...c } }))}
        />
      ) : null}

      {scheduling ? (
        <ScheduleFollowUpModal
          clientId={client.id}
          clientName={client.name}
          onClose={() => setScheduling(false)}
          onCreated={load}
        />
      ) : null}
    </div>
  );
}
